import React, { useState } from 'react'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { useAnalysisTypeAnalyses, useAnalyses } from 'redux/hooks/Analyses'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import { SxProps } from '@mui/material'

import { parseElapsedTimeSince } from 'helpers/Functions/Date'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SearchField from 'components_new/molecules/SearchField'
import SimpleTable from 'components_new/molecules/SimpleTable'
import ValueText from 'components_new/molecules/ValueText'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import SelectUser from 'components_new/organisms/SelectUser'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

interface AnalysesTableProps {
  /** Scopes the table to one analysis. Omitted, it lists every analysis. */
  analysisTypeId?: string
  /** Limits the table to these analyses. */
  enableSearch?: boolean
  ids?: string[]
  preview?: boolean
  stickyHeader?: boolean
  sx?: SxProps
  viewAllHref?: string
}

const AnalysesTable = (props: AnalysesTableProps) => {
  const {
    analysisTypeId,
    enableSearch,
    ids,
    preview,
    stickyHeader,
    sx,
    viewAllHref
  } = props

  const { t, translateEnum } = useTranslation()

  const all = useAnalyses()
  const scoped = useAnalysisTypeAnalyses(analysisTypeId ?? null)

  const { analyses: source, loading } = analysisTypeId ? scoped : all
  const analyses = ids
    ? source.filter((analysis) => ids.includes(analysis.id))
    : source

  const { isAdmin } = useAccountSettings()
  const { tryDeleteAnalysis, tryUpdateAnalysis } = useAnalysisActions()

  const [searchQuery, setSearchQuery] = useState('')
  const [deleteItem, setDeleteItem] = useState<{
    id: string
    title: string
  } | null>(null)

  const allowDelete = !preview && isAdmin

  const filteredAnalyses = analyses.filter((analysis) =>
    analysis.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Only a preview that links to the full list is cut short.
  const truncate = preview && Boolean(viewAllHref)

  const displayedAnalyses = truncate
    ? filteredAnalyses.slice(0, 3)
    : filteredAnalyses

  const hasMore = truncate && filteredAnalyses.length > 3

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...sx
      }}
    >
      {enableSearch ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: 1,
            mb: 1
          }}
        >
          <SearchField
            disabled={loading}
            onClear={() => setSearchQuery('')}
            onSearch={(value) => setSearchQuery(value)}
            placeholder={t('analyses', 'search_placeholder')}
            size="small"
            value={searchQuery}
          />
        </Box>
      ) : null}

      {!loading && analyses.length === 0 ? (
        <EmptyPlaceholder
          actions={
            <Button href={'/results'}>
              {t('global', 'go_to')} {t('global', 'overview', true)}
            </Button>
          }
          background={true}
          body={t('analyses', 'empty_body')}
          size="small"
          sx={{ flex: 1 }}
          title={t('analyses', 'empty_title')}
        />
      ) : (
        <SimpleTable
          loading={loading}
          columns={
            !truncate
              ? [
                  { title: t('analyses', 'analysis') },
                  { align: 'right', title: t('analyses', 'potential') },
                  { title: t('analyses', 'status_column') },
                  { title: t('global', 'responsible') },
                  { defaultSort: 'desc', title: t('global', 'created') },
                  ...(preview ? [] : [{ title: t('global', 'handled') }]),
                  ...(allowDelete ? [{ title: '' }] : [])
                ]
              : undefined
          }
          hover={true}
          rows={displayedAnalyses.map((analysis) => {
            const handled = analysis.is_handled
            const title = analysis.title

            return {
              cells: [
                <GenericListItem
                  body={analysis.description ?? undefined}
                  customTitle={
                    <Highlighted highlight={searchQuery} text={title} />
                  }
                  disablePadding={true}
                  icon={{
                    color: 'disabled',
                    name: 'Analysis'
                  }}
                  key="title"
                  sx={{
                    maxWidth: { xs: 256, xl: preview ? 400 : 512 }
                  }}
                  title={title}
                />,
                <ValueText
                  align="right"
                  color="success.text"
                  displayPositiveIndicator={true}
                  key="potential"
                  unit="kr/år"
                  value={analysis.potential}
                />,
                <Chip
                  color={handled ? undefined : 'primary'}
                  key="status"
                  label={translateEnum('analysis_status', analysis.status)}
                  size="xsmall"
                />,
                <SelectUser
                  key="owner"
                  onChange={(value) =>
                    tryUpdateAnalysis(analysis.id, {
                      responsible_account_id: value
                    })
                  }
                  size="small"
                  value={analysis.responsible_account_id}
                />,
                <Text
                  color="text.disabled"
                  component="span"
                  key="created_at"
                  noWrap={true}
                  variant="body2"
                >
                  {t('global', 'time_ago', true, {
                    time: parseElapsedTimeSince(analysis.created_at, t)
                  })}
                </Text>,
                ...(!preview
                  ? [
                      <Text
                        color="text.disabled"
                        component="span"
                        key="handled_at"
                        noWrap={true}
                        variant="body2"
                      >
                        {analysis.decided_at
                          ? t('global', 'time_ago', true, {
                            time: parseElapsedTimeSince(
                              analysis.decided_at,
                              t
                            )
                          })
                          : '-'}
                      </Text>
                    ]
                  : []),
                ...(allowDelete
                  ? [
                      <OptionsMenuButton
                        key="options"
                        options={[
                          {
                            color: 'error',
                            iconName: 'Delete',
                            onClick: () => setDeleteItem(analysis),
                            title: t('global', 'delete')
                          }
                        ]}
                        size="small"
                      />
                    ]
                  : [])
              ],
              href: `/analyses/${analysis.id}`,
              id: analysis.id,
              sortValues: [
                { value: analysis.title },
                { value: analysis.potential ?? 0 },
                { value: handled ? 'b' : 'a' },
                { value: undefined },
                { value: analysis.created_at },
                ...(preview ? [] : [{ value: analysis.decided_at ?? '' }])
              ]
            }
          })}
          size="small"
          stickyHeader={stickyHeader}
        />
      )}

      {preview && hasMore && viewAllHref ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            py: 1,
            px: 2
          }}
        >
          <Text color="text.secondary" variant="body2">
            {t('global', 'shows')} <b>3</b> {t('global', 'of', true)}{' '}
            {filteredAnalyses.length}
          </Text>
          <Link arrow={true} href={viewAllHref}>
            {t('global', 'show_all')}
          </Link>
        </Box>
      ) : null}

      {/*-- dialogs --*/}
      <DeleteDialog
        onClose={() => setDeleteItem(null)}
        onDelete={(callback) => {
          if (!deleteItem) return

          tryDeleteAnalysis(deleteItem.id, (err) => {
            if (!err) {
              setDeleteItem(null)
            }
            callback()
          })
        }}
        open={Boolean(deleteItem)}
        title={deleteItem?.title}
        type={t('analyses', 'analysis', true)}
      />
    </Box>
  )
}

export default AnalysesTable
